import { createContext, useContext, useEffect, useState } from 'react';
import useFeatureActions from '../hooks/APIHooks/users/useFeatureActions';
import { Feature } from '../types/features';

// datatype of the context declared here
interface IFeatureContext {
  featureList: Array<Feature>;
  targetedFeature: Feature | null;
  searchText: string;
  showDeleteModal: boolean;
  setTargetedFeature: React.Dispatch<React.SetStateAction<Feature | null>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

// creating the context here
const FeatureContext = createContext<IFeatureContext | null>(null);

const FeatureProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [searchText, setSearchText] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [featureList, setFeatureList] = useState<Array<Feature>>([]);
  const [targetedFeature, setTargetedFeature] = useState<Feature | null>(null);

  // integration of custom API hooks here
  const { getFeatureListHandler } = useFeatureActions('getFeatureList');

  // setting features into local state here
  useEffect(() => {
    if (getFeatureListHandler.data?.is_success) {
      setFeatureList(
        getFeatureListHandler.data.data.filter((feature) =>
          searchText
            ? feature.name.toLowerCase().includes(searchText.toLowerCase())
            : feature
        )
      );
    }
  }, [getFeatureListHandler.data, searchText]);

  // rendering the context provider here
  return (
    <FeatureContext.Provider
      value={{
        featureList,
        targetedFeature,
        searchText,
        showDeleteModal,
        setTargetedFeature,
        setSearchText,
        setShowDeleteModal,
        isError: getFeatureListHandler.isError,
        isLoading: getFeatureListHandler.isLoading,
        isSuccess: getFeatureListHandler.isSuccess,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};

export default FeatureProvider;

// custom hook to access the context declared here
export const useFeatureContext = () => {
  const context = useContext(FeatureContext);

  if (!context) {
    throw new Error('useFeatureContext must be within FeatureProvider!');
  }

  return context;
};
